// client/src/pages/PasswordReset/styles.js
import styled, { keyframes } from 'styled-components';

// ========== Animations ==========
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// ========== Layout ==========
export const PageContainer = styled.div`
  min-height: 100vh;
  background: #e1e1e1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  direction: rtl;
`;

export const FormCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 480px;
  animation: ${fadeIn} 0.4s ease-out;
`;

export const FormHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

export const IconWrapper = styled.div`
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin: 0 auto 20px;
`;

export const Title = styled.h1`
  color: #333;
  font-size: 1.8rem;
  margin: 0 0 8px;
  font-weight: 600;
`;

export const Subtitle = styled.p`
  color: #666;
  font-size: 0.95rem;
  margin: 0;
`;

// ========== Form Elements ==========
export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
`;

export const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  background: #f8f9fa;
  color: #333;
  font-size: 1rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    background: white;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #999;
  }
`;

// ========== Button ==========
export const SubmitButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

export const BackLink = styled.button`
  background: none;
  border: none;
  color: #667eea;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 8px;
  margin-top: 16px;
  width: 100%;
  text-align: center;
  transition: color 0.2s;

  &:hover {
    color: #764ba2;
    text-decoration: underline;
  }
`;

// ========== Alerts ==========
export const Alert = styled.div`
  padding: 14px 18px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
  animation: ${fadeIn} 0.3s ease-out;

  ${(props) =>
    props.type === 'error' &&
    `
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
  `}

  ${(props) =>
    props.type === 'success' &&
    `
    background: #d1fae5;
    color: #065f46;
    border: 1px solid #a7f3d0;
  `}
`;

export const AlertIcon = styled.span`
  font-size: 1.2rem;
`;

// ========== Status Cards ==========
export const StatusCard = styled.div`
  background: white;
  padding: 48px 40px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 480px;
  width: 100%;
  animation: ${fadeIn} 0.4s ease-out;
`;

export const StatusIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  margin: 0 auto 24px;
  animation: ${pulse} 2s ease-in-out infinite;

  ${(props) =>
    props.type === 'success' &&
    `
    background: #d1fae5;
  `}

  ${(props) =>
    props.type === 'error' &&
    `
    background: #fee2e2;
  `}
`;

export const StatusTitle = styled.h2`
  font-size: 1.5rem;
  margin: 0 0 12px;
  font-weight: 600;

  ${(props) =>
    props.type === 'success' &&
    `
    color: #27ae60;
  `}

  ${(props) =>
    props.type === 'error' &&
    `
    color: #e74c3c;
  `}
`;

export const StatusMessage = styled.p`
  color: #666;
  font-size: 1rem;
  margin: 0 0 24px;
  line-height: 1.6;
`;

export const ActionButton = styled.button`
  padding: 12px 32px;
  background: ${(props) =>
    props.variant === 'primary'
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : '#f8f9fa'};
  color: ${(props) => (props.variant === 'primary' ? 'white' : '#333')};
  border: ${(props) =>
    props.variant === 'primary' ? 'none' : '1px solid #e9ecef'};
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${(props) =>
      props.variant === 'primary'
        ? '0 4px 12px rgba(102, 126, 234, 0.3)'
        : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  }
`;

// ========== Loading ==========
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.span`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

// ========== Password Strength (Optional Enhancement) ==========
export const PasswordStrength = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 8px;
`;

export const StrengthBar = styled.div`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: ${(props) => {
    if (!props.active) return '#e9ecef';
    if (props.strength === 'weak') return '#e74c3c';
    if (props.strength === 'medium') return '#f39c12';
    return '#27ae60';
  }};
  transition: background 0.3s ease;
`;

export const StrengthText = styled.span`
  font-size: 0.75rem;
  color: ${(props) => {
    if (props.strength === 'weak') return '#e74c3c';
    if (props.strength === 'medium') return '#f39c12';
    return '#27ae60';
  }};
  margin-top: 4px;
`;
